/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ActiveRevisionSelect.tsx
 * Project: mle-client-n
 * Committed last: 2022/11/22 @ 166
 * ---
 * Description:
 */

import {Button, Intent} from '@blueprintjs/core';
import {MyLabelRevision} from '../../../../entities';
import {useCallback, useEffect, useState} from 'react';
import {mleDomainGqlClient} from '../../../../api/domain';
import {GET_REVISION_INFO, GET_REVISIONS_INFO} from '../../../../api/domain/queries.gql';
import {HasEditor} from '../../../../common/props';
import {observer} from 'mobx-react';
import {mlAuthSecured} from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';
import {MleToaster} from '../../../../common/toast';
import {RevisionSelect} from "./RevisionSelect";

interface Props extends HasEditor {
    onRevisionSelect: (recipe: MyLabelRevision) => void;
    disabled?: boolean;
}

export const ActiveRevisionSelect = observer((props: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [revisions, setRevisions] = useState<MyLabelRevision[]>([]);
    const [selectedRevision, setSelectedRevision] = useState<MyLabelRevision | undefined>();
    const [noRevision, setNoRevision] = useState(false);

    const showErrorDialog = () => {
        MleToaster.getInstance().show({
            message: 'Impossibile caricare revisioni',
            intent: Intent.DANGER,
            icon: 'error'
        });
    };

    useEffect(() => {
            (async () => {
                const revisionSwitcher = props.editor.recipeSwitcher;

                if (!revisionSwitcher.hasActiveCompany) return;
                const activeCompany = props.editor.recipeSwitcher.activeCompany;

                setIsLoading(true);

                // Load revisions meta
                const revisionsList = await mlAuthSecured<MyLabelRevision[]>(props.editor,
                    async () => {
                        return (await mleDomainGqlClient.query({
                            query: GET_REVISIONS_INFO,
                            variables: {
                                companyId: activeCompany
                            },
                            fetchPolicy: 'no-cache'
                        })).data.getLabelRevisions as MyLabelRevision[];
                    });

                // Check returned result
                if (revisionsList)
                    setRevisions(revisionsList);

                if (revisionsList && revisionsList?.length == 0)
                    setNoRevision(true);
                else if (revisionsList && revisionsList?.length > 0)
                    setNoRevision(false);

                // Set the selected revision if there is one
                if (props.editor.recipeSwitcher.hasActiveRevision) {
                    // Get the revision data from the id
                    const recipeData = await mlAuthSecured<MyLabelRevision>(props.editor, async () => {
                        return (await mleDomainGqlClient.query({
                            query: GET_REVISION_INFO,
                            variables: {
                                revisionId: props.editor.recipeSwitcher.activeRevision,
                                companyId: props.editor.recipeSwitcher.activeCompany
                            }
                        })).data.getLabelRevision as MyLabelRevision;
                    });

                    if (recipeData)
                        setSelectedRevision(recipeData);
                    else
                        showErrorDialog();
                }
                setIsLoading(false);
            })();
        },
        [props.editor.recipeSwitcher.activeCompany, setRevisions,
            props.editor.recipeSwitcher.activeRevision, props.editor.recipeSwitcher]);

    const handleSelectItem = useCallback((item: MyLabelRevision) => {
        setSelectedRevision(item);
        props.onRevisionSelect(item);
    }, [setSelectedRevision]);

    return (
        <div>
            {
                !noRevision && <RevisionSelect
                    selectedValue={selectedRevision}
                    onChange={handleSelectItem}
                    data={revisions}
                    isLoading={isLoading}
                    disabled={noRevision || !props.editor.recipeSwitcher.hasActiveCompany}/>
            }
            {
                noRevision && <Button disabled text="Nessuna revisione" icon="warning-sign"/>
            }
        </div>
    );
});
